import { Fragment, useEffect, useState } from "react";
import { capitalise, dedupe } from "lib/useful";
import { collect, store } from "react-recollect";
import useStoreLoader from "lib/useStoreLoader";
import Loading from "components/Loading";
import LoadResearches from "components/loaders/LoadResearches";
import LoadResearchData from "components/loaders/LoadResearchData";
import LoadShopifyOrders from "components/loaders/LoadShopifyOrders";
import OrderLine from "components/OrderLine";
import { API_URL } from "lib/API";
import Page from "components/Page";
import FullsizeMessage from "components/FullsizeMessage";
import { format } from "date-fns";
import MessageDialogWithActions from "components/MessageDialogWithActions";
import {
  FulfilmentHeaderBar,
  FulfilmentOrderLine,
} from "components/FulfilmentComponents";

function FulfilmentList({ product, text }) {
  const { isLoading, items, refresh } = useStoreLoader({
    collection: `fulfilments`,
    storeKey: `fulfilments-${product}`,
    find: { product, completed: null },
  });
  useEffect(
    () => () => {
      delete store[`fulfilments-${product}`];
    },
    [product],
  ); // force it to reload next time

  const [imageReloadCounter, setImageReloadCounter] = useState(1);

  const YMD = format(new Date(), "yyyy-MM-dd");

  const [printDialog, setPrintDialog] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  function setPrintDialogURL(type, fulfilment, actions = []) {
    return setPrintDialog({
      message: (
        <iframe
          width="100%"
          height="600px"
          className="h-[80vh]"
          title="Certificate"
          src={`${API_URL}pdfFulfilment-${type}/${fulfilment._id}/${type}_${(fulfilment.certNumber ?? fulfilment.name ?? YMD).replaceAll("/", "_")}.pdf?token=${store.token}&print`}
        ></iframe>
      ),
      actions,
    });
  }

  const fulfilments =
    Object.values(items ?? {}).filter((f) => !f.complete) ?? [];

  // load research, research data, shopify order for each fulfilment
  const researchesToLoad = dedupe(
    fulfilments.flatMap((f) =>
      !store.certificateResearch?.[f.researchId] ? f.researchId : [],
    ),
  );
  const researchDataToLoad = dedupe(
    fulfilments.flatMap((f) =>
      !store.researchDataByCertId?.[f.researchId] ? f.researchId : [],
    ),
  );
  const shopifyOrdersToLoad = dedupe(
    fulfilments.flatMap((f) =>
      !store.shopifyOrders?.[f.shopifyOrdersId] ? f.shopifyOrdersId : [],
    ),
  );

  return isLoading ? (
    <Loading text={`Loading items ready to be ${text}`} />
  ) : researchesToLoad.length > 0 ? (
    <LoadResearches ids={researchesToLoad} />
  ) : researchDataToLoad.length > 0 ? (
    <LoadResearchData ids={researchDataToLoad} />
  ) : shopifyOrdersToLoad.length > 0 ? (
    <LoadShopifyOrders ids={shopifyOrdersToLoad} />
  ) : fulfilments.length === 0 ? (
    <FullsizeMessage>No orders waiting to be {text}</FullsizeMessage>
  ) : (
    <Page title={capitalise(product)} heading={`To be ${text}`}>
      {sendingAll || (
        <>
          <FulfilmentHeaderBar
            product={product}
            setSendingAll={setSendingAll}
          />
          <div>
            {printDialog && (
              <MessageDialogWithActions
                name="PrintDialog"
                wide
                message={printDialog.message}
                actions={[
                  { text: "OK", onClick: () => setPrintDialog(null) },
                  ...(printDialog.actions ?? []),
                ]}
              />
            )}
            {fulfilments.map((fulfilment, fulfilmentIndex) => (
              <Fragment key={fulfilmentIndex}>
                {fulfilment.complete ? (
                  <p key={fulfilment._id} className="ml-2 mb-1">
                    Fulfilment complete {fulfilment._id}
                  </p>
                ) : (
                  <OrderLine
                    key={fulfilment._id}
                    r={fulfilment}
                    extraContentLeft={
                      fulfilment.certsInOrder > 1 && (
                        <p>
                          ({fulfilment.certNumberWithinOrder + 1} of{" "}
                          {fulfilment.certsInOrder})
                        </p>
                      )
                    }
                  >
                    <FulfilmentOrderLine
                      product={product}
                      fulfilment={fulfilment}
                      setPrintDialog={setPrintDialog}
                      setPrintDialogURL={setPrintDialogURL}
                      refresh={refresh}
                      imageReloadCounter={imageReloadCounter}
                      setImageReloadCounter={setImageReloadCounter}
                    />
                  </OrderLine>
                )}
              </Fragment>
            ))}
          </div>
        </>
      )}
    </Page>
  );
}

export default collect(FulfilmentList);
